import type { ContractId } from './contracts'

export type AchievementId = 'FIRST_CONTRACT' | 'CONTRACT_CHAIN' | 'DAILY_OPERATOR' | 'S_RANK' | 'SCORE_BREAKER'

export interface DailyOperation {
  readonly id: string
  readonly dateKey: string
  readonly title: string
  readonly briefing: string
  readonly contractId: ContractId
  readonly bonusXp: number
  readonly scoreBonus: number
}

export interface AchievementDefinition {
  readonly id: AchievementId
  readonly name: string
  readonly description: string
  readonly rewardXp: number
  readonly glyph: string
}

const OPERATION_ROTATION: readonly Omit<DailyOperation, 'id' | 'dateKey'>[] = [
  { title: 'IMMUTABLE DAWN', briefing: 'Keep every Core shard online during today’s operation.', contractId: 'PERFECT_LEDGER', bonusXp: 90, scoreBonus: 1_250 },
  { title: 'ACCELERATED CLEARING', briefing: 'Convert preparation time into liquidity before settlement.', contractId: 'RAPID_SETTLEMENT', bonusXp: 80, scoreBonus: 1_100 },
  { title: 'MEMPOOL PURGE', briefing: 'Remove hostile processes before they reach finality.', contractId: 'CLEAN_SWEEP', bonusXp: 85, scoreBonus: 1_175 },
]

export const ACHIEVEMENTS: Record<AchievementId, AchievementDefinition> = {
  FIRST_CONTRACT: { id: 'FIRST_CONTRACT', name: 'SIGNED & SEALED', description: 'Complete your first Challenge Contract.', rewardXp: 75, glyph: '◇' },
  CONTRACT_CHAIN: { id: 'CONTRACT_CHAIN', name: 'HOT STREAK', description: 'Complete three contracts in succession.', rewardXp: 150, glyph: '≋' },
  DAILY_OPERATOR: { id: 'DAILY_OPERATOR', name: 'FIELD OPERATOR', description: 'Complete a rotating daily operation.', rewardXp: 100, glyph: '⌁' },
  S_RANK: { id: 'S_RANK', name: 'APEX DEFENDER', description: 'Finish a run with an S performance rank.', rewardXp: 200, glyph: 'S' },
  SCORE_BREAKER: { id: 'SCORE_BREAKER', name: 'FIVE FIGURES', description: 'Score at least 10,000 points in one run.', rewardXp: 125, glyph: '↗' },
}

export const ACHIEVEMENT_IDS = Object.keys(ACHIEVEMENTS) as AchievementId[]

export function getDailyOperation(date = new Date()): DailyOperation {
  const dateKey = localDateKey(date)
  const dayIndex = Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000)
  const operation = OPERATION_ROTATION[Math.abs(dayIndex) % OPERATION_ROTATION.length]
  return { ...operation, id: `OP-${dateKey}`, dateKey }
}

export function isConsecutiveOperation(previousId: string | null, currentDateKey: string): boolean {
  if (!previousId) return false
  const previousKey = previousId.replace('OP-', '')
  const previous = new Date(`${previousKey}T12:00:00`)
  const current = new Date(`${currentDateKey}T12:00:00`)
  return Math.round((current.getTime() - previous.getTime()) / 86_400_000) === 1
}

function localDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
